<template>
  <div id="auth-view">
    <div class="ispinner white animating" v-if="!showForm">
      <div class="ispinner-blade"></div>
      <div class="ispinner-blade"></div>
      <div class="ispinner-blade"></div>
      <div class="ispinner-blade"></div>
      <div class="ispinner-blade"></div>
      <div class="ispinner-blade"></div>
      <div class="ispinner-blade"></div>
      <div class="ispinner-blade"></div>
    </div>
    <div class="form" v-else>
      <!-- 로그인된 상태 -->
      <div v-if="user" class="logged-in-content">
        <header>
          <div class="icon">
            <h1>Shadow</h1>
          </div>
          <h2>Welcome to Shadow!<br />Your download has started.</h2>
          <p>
            Install the app to get started.<br />Didn't see the download? Click
            the button below.
          </p>
        </header>
        <main>
          <div class="btn-group-wrap">
            <button id="btn-download" @click="download">Download now</button>
          </div>
          <!-- <div class="btn-group-wrap">
            <button id="btn-signout" @click="signout">Sign Out</button>
          </div> -->
        </main>
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="hasError" class="error-content">
        <header>
          <div class="icon">
            <h1>Shadow</h1>
          </div>
          <h2 v-if="errorType === 'already_member'">
            You're already a Shadow member ✨
          </h2>
          <h2 v-else>Oops, something went wrong</h2>
          <p v-if="errorType === 'already_member'">
            This invite is for new members only.<br />Open Shadow to keep going.
          </p>
          <p v-else>
            Please try again in a moment.<br />
            If the issue continues, contact us at support@shadow.do.
          </p>
        </header>
        <main>
          <div class="btn-group-wrap">
            <button id="btn-go-back" @click="resetError">Go back</button>
          </div>
        </main>
      </div>

      <!-- 로그인 안된 상태 -->
      <div v-else class="login-content">
        <header>
          <div class="icon">
            <h1>Shadow</h1>
          </div>
          <h2>
            You've been invited to Shadow!<br />Sign up now and get your first
            month free.
          </h2>
          <p>Just sign in with the email you'll use in the app</p>
          <p class="invited-by">
            You are invited by<br /><strong>{{ hostEmail }}</strong>
          </p>
        </header>
        <main>
          <div class="auth-buttons">
            <div class="btn-group-wrap">
              <button id="btn-google" @click="loginWithGoogle">
                Continue with Google
              </button>
            </div>
            <div class="btn-group-wrap">
              <button id="btn-email" @click="openModal">
                Continue with Email
              </button>
            </div>
          </div>

          <div class="terms">
            <p>
              By continuing you acknowledge that you have <br />
              read, understood, and agree to Shadow's<br />
              <a
                href="https://www.shadow.do/blog/terms-of-service"
                class="terms-link"
                target="_blank"
                >Terms of Service</a
              >
              and
              <a
                href="https://www.shadow.do/blog/privacy-policy"
                class="terms-link"
                target="_blank"
                >Privacy Policy</a
              >.
            </p>
          </div>
        </main>
      </div>

      <!-- 이메일 모달 -->
      <div v-if="showEmailModal" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="before" v-if="!emailSent">Enter your email</h3>
            <h3 class="after" v-else>Check your inbox!</h3>
            <p class="before" v-if="!emailSent">
              You will receive an email with a sign-in link.
            </p>
            <p v-else class="after">
              Just click the link we sent to <br />
              <strong>{{ email }}</strong> to sign in!
            </p>
          </div>

          <div v-if="!emailSent" class="email-input-group">
            <input
              :value="email"
              type="email"
              ref="emailInput"
              placeholder="john.doe@acme.co"
              class="email-input"
              @input="filterEmailInput"
              @keyup.enter="handleEmailAuth"
            />
          </div>

          <div :class="['modal-buttons', { after: emailSent }]">
            <button @click="closeModal" id="dismiss-btn">Dismiss</button>
            <button
              v-if="!emailSent"
              @click="handleEmailAuth"
              id="confirm-btn"
              :disabled="!isEmailValid"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, googleProvider } from "../firebase.js";

export default {
  name: "AuthView",
  data() {
    return {
      referralCode: null,
      showForm: false,
      hostEmail: "",
      user: null,
      email: "",
      hasError: false,
      showEmailModal: false,
      emailSent: false,
      emailSentOnce: false, // 이메일을 한번이라도 전송했는지 추적
      googleLoginAttempted: false, // 구글 로그인 시도 여부
      apiBaseUrl: "https://d3ea3io1axhl6i.cloudfront.net/dev", // API 베이스 URL
      errorType: "general", // 'general' 또는 'already_member'
    };
  },
  computed: {
    isEmailValid() {
      // 2글자 이상이면서 @와 .을 모두 포함하는지 확인
      return (
        this.email.length >= 2 &&
        this.email.includes("@") &&
        this.email.includes(".")
      );
    },
  },
  async mounted() {
    await this.signout();

    onAuthStateChanged(auth, async (firebaseUser) => {
      if (this.hasError) return;

      if (firebaseUser != null) {
        if (this.googleLoginAttempted) this.emailSentOnce = false;

        if (this.googleLoginAttempted || !this.emailSentOnce) {
          // Firebase 인증 성공 후 서비스 로그인 시도
          await this.attemptServiceLogin(firebaseUser);
          this.googleLoginAttempted = false;
        } else {
          // location.replace("https://shadow.do");
        }
      }
    });

    try {
      if (this.$route.path.startsWith("/r/")) {
        // /r/:code에서 접근한 경우
        this.referralCode = this.$route.params.code;

        if (this.referralCode) {
          // URL을 /auth/invite로 변경 (히스토리 교체)
          this.$router.replace("/auth/invite");

          // code 처리 로직
          let verifyReferralCodeResult = await this.verifyReferralCode(
            this.referralCode
          );
          if (!verifyReferralCodeResult) {
            throw "Invalid access";
          }

          this.showForm = true;
        } else {
          throw "Invalid access";
        }
      } else if (this.$route.path === "/auth/invite") {
        const urlParams = new URLSearchParams(window.location.search);

        // /auth/invite로 직접 접근한 경우
        // code가 이미 처리되었는지 확인 (위에서 replace로 온 경우)
        if (!this.referralCode && !urlParams.has("oobCode")) {
          throw "Invalid access";
        } else {
          // 이메일 링크로 접속했는지 확인
          if (isSignInWithEmailLink(auth, window.location.href)) {
            await this.handleEmailLinkSignIn();
          }
        }
      }
    } catch (error) {
      console.log(`Error: ${error}`);

      this.hasError = true;
      this.showForm = true;
    }
  },
  methods: {
    async verifyReferralCode(code) {
      localStorage.removeItem("referralCode");
      this.referralToken = null; // 기존 토큰 초기화

      if ((code ?? "").length == 0) {
        return false;
      }

      try {
        // API 호출
        const response = await fetch(
          `${this.apiBaseUrl}/referral/referral-code`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ referralCode: code }),
          }
        );

        if (!response.ok) {
          // 에러 응답 처리
          console.log("Referral code verification failed:", response.status);
          this.hasError = true;
          this.errorType = "general";
          return false;
        }

        // 성공 응답 처리
        const data = await response.json();

        // JSON 응답에서 값 추출하여 변수에 저장
        this.hostEmail = data.hostEmail || "";

        if (this.hostEmail == "") throw "Failed to load host info";

        // 레퍼럴 토큰 저장 (인스턴스 변수에 저장)
        if (data.referralToken) {
          this.referralToken = data.referralToken;
          console.log("Referral token saved");
        }

        console.log("Referral code verified successfully");

        return true;
      } catch (error) {
        // 네트워크 에러나 기타 예외 처리
        console.log("Error verifying referral code:", error);
        this.hasError = true;
        this.errorType = "general";
        return false;
      }
    },
    // 서비스 로그인 시도
    async attemptServiceLogin(firebaseUser) {
      try {
        this.showForm = false;

        // Firebase ID 토큰 획득
        const idToken = await firebaseUser.getIdToken();

        // 로그인 시도
        const loginResponse = await this.serviceSignIn(idToken);

        if (loginResponse.ok) {
          // 로그인 성공 - 이미 회원인 경우
          this.errorType = "already_member";
          this.hasError = true;
          this.showForm = true;
        } else {
          // 로그인 실패 시 에러 처리
          const errorData = await loginResponse.json();
          throw errorData.message || "Login failed";
        }
      } catch (error) {
        console.log("Service login error:", error);

        // 에러 메시지에서 'Unauthorized' 체크 (Dart 코드의 로직을 JS로 변환)
        const errorMessage = (
          typeof error === "string" ? error : error.message || error.toString()
        ).replace(/^\[[^\]]+\]\s*/, "");

        if (errorMessage === "Unauthorized") {
          // 회원가입 시도
          try {
            await this.serviceSignUp(firebaseUser);
            this.showForm = true;
            this.user = firebaseUser;
            this.download();
          } catch (signUpError) {
            console.log("Service sign up error:", signUpError);
            this.errorType = "general";
            this.hasError = true;
          }
        } else {
          // 기타 에러
          console.log("Service authentication error:", error);
          this.errorType = "general";
          this.hasError = true;
        }

        this.showForm = true;
      }
    },

    // 서비스 로그인 API 호출
    async serviceSignIn(idToken) {
      const userAgent = navigator.userAgent;

      const params = {
        userDeviceToken: "", // 필요시 디바이스 토큰 추가
        userAgent: userAgent,
      };

      const response = await fetch(`${this.apiBaseUrl}/sign-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(params),
      });

      return response;
    },

    // 서비스 회원가입 API 호출
    async serviceSignUp(firebaseUser) {
      const idToken = await firebaseUser.getIdToken();
      let params = {
        referralToken: this.referralToken,
      };

      // 사용자 정보 수집
      const firebaseUserInfo = {
        providerId: firebaseUser.providerData[0]?.providerId || "",
        displayName: firebaseUser.displayName,
        email: firebaseUser.email,
      };

      if (firebaseUserInfo.providerId.includes("google.com")) {
        // Google 사용자 정보 가져오기
        try {
          const googleUserInfoResponse = await fetch(
            "https://www.googleapis.com/oauth2/v3/userinfo",
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
              },
            }
          );
          const googleUserInfo = await googleUserInfoResponse.json();

          params.userDisplayNameOrigin = firebaseUserInfo.displayName || "";
          params.userDisplayName =
            googleUserInfo.given_name ||
            firebaseUserInfo.displayName ||
            firebaseUserInfo.email.split("@")[0];
          params.userFamilyName = googleUserInfo.family_name || "";
          params.userGivenName = googleUserInfo.given_name || "";
        } catch (error) {
          console.log("Failed to get Google user info:", error);
          // 기본 정보로 대체
          params.userDisplayNameOrigin = firebaseUserInfo.displayName || "";
          params.userDisplayName =
            firebaseUserInfo.displayName ||
            firebaseUserInfo.email.split("@")[0];
          params.userFamilyName = "";
          params.userGivenName = "";
        }
      } else if (firebaseUserInfo.providerId.includes("password")) {
        // 이메일 로그인 사용자
        params.userDisplayNameOrigin = firebaseUserInfo.displayName || "";
        params.userDisplayName =
          firebaseUserInfo.displayName || firebaseUserInfo.email.split("@")[0];
        params.userFamilyName = "";
        params.userGivenName = "";
      }

      const response = await fetch(`${this.apiBaseUrl}/sign-up`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(params),
      });

      // 성공 시에는 status code만 확인
      if (response.ok) {
        console.log("Sign-up successful");

        // 회원가입 성공 후 레퍼럴 토큰 초기화
        this.referralToken = null;
        console.log("Referral token cleared after successful signup");

        return; // 성공 시 아무것도 반환하지 않음
      }

      // 에러 시에만 응답 텍스트 확인
      const responseText = await response.text();
      let errorMessage = "Sign up failed";

      if (responseText) {
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // JSON 파싱 실패 시 응답 텍스트 자체를 사용
          errorMessage = `Server error (${response.status}): ${responseText}`;
        }
      } else {
        errorMessage = `Server error (${response.status}): No response body`;
      }

      throw new Error(errorMessage);
    },

    filterEmailInput(event) {
      // 허용되는 문자: a-z, A-Z, 0-9, @, ., _, -
      const allowedChars = /[a-zA-Z0-9@._-]/g;
      const inputValue = event.target.value;

      // 허용되지 않는 문자 제거
      const filteredValue = inputValue.match(allowedChars)?.join("") || "";

      // 항상 email 데이터와 input 값을 동기화
      this.email = filteredValue;

      // DOM 값도 필터링된 값으로 설정
      if (event.target.value !== filteredValue) {
        event.target.value = filteredValue;
      }
    },

    async loginWithGoogle() {
      try {
        this.hasError = false;
        this.googleLoginAttempted = true; // 구글 로그인 시도 플래그 설정
        await signInWithPopup(auth, googleProvider);
      } catch (err) {
        if (
          err.code == "auth/cancelled-popup-request" ||
          err.code == "auth/popup-closed-by-user"
        ) {
          this.googleLoginAttempted = false; // 취소된 경우 플래그 리셋
          return;
        }

        console.log("Failed to Google sign in:", err);
        this.errorType = "general";
        this.hasError = true;
        this.googleLoginAttempted = false; // 실패한 경우 플래그 리셋
      }
    },

    async handleEmailAuth() {
      if (!this.isEmailValid) {
        location.replace("https://shadow.do");
      }

      try {
        this.hasError = false;

        const actionCodeSettings = {
          url: window.location.href, // 현재 페이지로 다시 돌아오도록
          handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(auth, this.email, actionCodeSettings);

        // 이메일을 로컬 스토리지에 저장 (나중에 로그인할 때 필요)
        localStorage.setItem("emailForSignIn", this.email);
        localStorage.setItem("referralCode", this.referralCode);

        this.emailSent = true;
        this.emailSentOnce = true; // 이메일을 한번이라도 전송했다는 플래그 설정
      } catch (err) {
        console.log("Failed to send email sign in link:", err);
        this.errorType = "general";
        this.hasError = true;
        this.showEmailModal = false;
      }
    },

    async handleEmailLinkSignIn() {
      try {
        let email = localStorage.getItem("emailForSignIn");
        let referralCode = localStorage.getItem("referralCode");

        let verifyReferralCodeResult = await this.verifyReferralCode(
          referralCode
        );
        if (!verifyReferralCodeResult) {
          throw "Invalid access";
        }

        if (email) {
          await signInWithEmailLink(auth, email, window.location.href);

          localStorage.removeItem("emailForSignIn");

          // URL에서 이메일 링크 관련 파라미터 제거
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.search
          );
        } else {
          throw "There is no valid email";
        }
      } catch (err) {
        console.log("Failed to email sign in:", err);
        this.errorType = "general";

        this.hasError = true;
        this.showForm = true;
      }
    },

    download() {
      const link = document.createElement("a");
      link.href = "https://web.shadow.do/download";
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async signout() {
      try {
        await signOut(auth);
      } catch (err) {
        console.log("Failed to sign out:", err);
        this.errorType = "general";
        this.hasError = true;
      }
    },

    resetError() {
      if (this.referralCode) {
        this.hasError = false;
        this.errorType = "general";
        this.clearForm();
      } else {
        location.replace("https://shadow.do");
      }
    },

    openModal() {
      this.showEmailModal = true;

      this.$nextTick(() => {
        if (this.$refs.emailInput) {
          this.$refs.emailInput.focus();
        }
      });
    },

    closeModal() {
      this.showEmailModal = false;
      this.email = "";
      this.emailSent = false;
    },

    clearForm() {
      this.email = "";
      this.emailSent = false;
      // emailSentOnce는 한번 true가 되면 세션동안 유지
      // googleLoginAttempted는 필요에 따라 개별적으로 관리
    },
  },
};
</script>

<style>
body:has(#auth-view) {
  background: #292a29;
  color: #fff;
}
</style>

<style scoped>
#auth-view {
  max-width: 720px;
  margin: 0 auto;
  padding: 5.587% 6.9835%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

header {
  margin-bottom: 24px;
  text-align: center;
}

header .icon {
  margin-bottom: 60px;
}

header .icon h1 {
  width: 120px;
  height: 31.2px;
  text-indent: -9999px;
  margin: 0 auto;
  background-image: url("@/assets/img/logo.png");
  background-size: cover;
  border-radius: 4px;
}

header h2 {
  font-size: 18px;
  line-height: 32px;
  font-weight: 400;
  color: #fff;
}

header p {
  margin: 24px 0;
  font-size: 14px;
  line-height: 25px;
  font-weight: 200;
  color: #aaa;
}

header p.invited-by {
  margin: 0;
  font-size: 14px;
  line-height: 25px;
  font-weight: 200;
  color: #eee;
}

header p.invited-by strong {
  font-weight: 600;
}

button {
  position: relative;
  font-size: 14px;
  font-weight: 400;
  line-height: 25px;
  padding: 0 6px;
  border-radius: 7px;
  color: #aaa;
  cursor: pointer;
  text-align: center;
}

div.btn-group-wrap {
  margin-top: 12px;
  text-align: center;
}
div.btn-group-wrap:first-child {
  margin-top: 0;
}

#btn-google,
#btn-download,
#btn-go-back {
  width: 300px;
  height: 37px;
  border: 0.5px solid #bb4a00;
  background: #bb4a00;
  color: #fff;
}

#btn-email,
#btn-signout {
  width: 300px;
  height: 37px;
  border: 0.5px solid #777;
  background: transparent;
  color: #aaa;
}

.auth-buttons {
  margin-bottom: 40px;
}

.terms {
  text-align: center;
  font-size: 11px;
  font-weight: 300;
  color: #777;
  line-height: 20px;
}

.terms-link {
  text-decoration: underline;
}

/* 로그인된 상태 스타일 */
.user-info {
  margin: 30px 0;
  padding: 20px;
  background: #1d2021;
  border-radius: 7px;
}

.profile-pic {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-bottom: 15px;
}

.user-name {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
}

.user-email {
  color: #999;
  font-size: 14px;
}

/* 모달 스타일 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #2c2c2c;
  border-radius: 10px;
  padding: 30px;
  max-width: 360px;
  width: 100%;
  margin: 10px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  text-align: center;
}

.modal-header .before {
  text-align: left;
}

.modal-header .after {
  text-align: center;
}

.modal-header h3 {
  font-size: 16px;
  line-height: 29px;
  font-weight: 400;
  color: #fff;
}

.modal-header p {
  color: #ccc;
  font-size: 14px;
  line-height: 25px;
  font-weight: 300;
  margin: 12px 0 20px;
}

.modal-header p strong {
  font-weight: 700;
}

.modal-header p.after {
  margin: 20px 0 30px;
}

.email-input-group {
  margin-bottom: 23px;
}

.email-input {
  width: 100%;
  padding: 8px 12px;
  background: #353535;
  border-radius: 7px;
  color: #eee;
  font-size: 14px;
  line-height: 25px;
  outline: none;
  transition: border-color 0.2s;
  border: none;
  caret-color: #fe8019;
}

.email-input:focus {
  border-color: #d65d0e;
}

.email-input::placeholder {
  color: #777777;
  font-weight: 200;
}

.modal-buttons {
  display: flex;
  justify-content: right;
  gap: 12px;
}

.modal-buttons.after {
  justify-content: center;
}

.modal-buttons button {
  padding: 5.5px 18px;
  border-radius: 7px;
  font-size: 14px;
  line-height: 25px;
  cursor: pointer;
  color: #eee;
  background: #353535;
}

#dismiss-btn {
  border: 0.5px solid #646464;
}

#confirm-btn {
  background: #bb4a00;
  border: 0.5px solid #bb4a00;
  transition: 0.2s;
}

#confirm-btn:disabled {
  background: #353535;
  border-color: #353535;
  cursor: not-allowed;
  opacity: 0.6;
}

.ispinner {
  position: relative;
  display: block;
  width: 20px;
  height: 20px;
  margin: 0 auto;
}
.ispinner .ispinner-blade {
  position: absolute;
  left: 44.5%;
  top: 37%;
  width: 10%;
  height: 35%;
  border-radius: 50%/20%;
  -webkit-animation: ispinner-fade 1s linear infinite;
  animation: ispinner-fade 1s linear infinite;
  -webkit-animation-play-state: paused;
  animation-play-state: paused;
}
.ispinner .ispinner-blade:nth-child(1) {
  -webkit-animation-delay: -1.66667s;
  animation-delay: -1.66667s;
  -webkit-transform: rotate(30deg) translate(0, -150%);
  transform: rotate(0) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(2) {
  -webkit-animation-delay: -1.58333s;
  animation-delay: -1.58333s;
  -webkit-transform: rotate(60deg) translate(0, -150%);
  transform: rotate(45deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(3) {
  -webkit-animation-delay: -1.5s;
  animation-delay: -1.5s;
  -webkit-transform: rotate(90deg) translate(0, -150%);
  transform: rotate(90deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(4) {
  -webkit-animation-delay: -1.41667s;
  animation-delay: -1.41667s;
  -webkit-transform: rotate(120deg) translate(0, -150%);
  transform: rotate(135deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(5) {
  -webkit-animation-delay: -1.33333s;
  animation-delay: -1.33333s;
  -webkit-transform: rotate(150deg) translate(0, -150%);
  transform: rotate(180deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(6) {
  -webkit-animation-delay: -1.25s;
  animation-delay: -1.25s;
  -webkit-transform: rotate(180deg) translate(0, -150%);
  transform: rotate(225deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(7) {
  -webkit-animation-delay: -1.16667s;
  animation-delay: -1.16667s;
  -webkit-transform: rotate(210deg) translate(0, -150%);
  transform: rotate(270deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(8) {
  -webkit-animation-delay: -1.08333s;
  animation-delay: -1.08333s;
  -webkit-transform: rotate(240deg) translate(0, -150%);
  transform: rotate(315deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(9) {
  -webkit-animation-delay: -1s;
  animation-delay: -1s;
  -webkit-transform: rotate(270deg) translate(0, -150%);
  transform: rotate(270deg) translate(0, -150%);
}
.ispinner .ispinner-blade:nth-child(10) {
  -webkit-animation-delay: -0.91667s;
  animation-delay: -0.91667s;
  -webkit-transform: rotate(300deg) translate(0, -150%);
  transform: rotate(300deg) translate(0, -150%);
}
.ispinner .ispinner-blade:nth-child(11) {
  -webkit-animation-delay: -0.83333s;
  animation-delay: -0.83333s;
  -webkit-transform: rotate(330deg) translate(0, -150%);
  transform: rotate(330deg) translate(0, -150%);
}
.ispinner .ispinner-blade:nth-child(12) {
  -webkit-animation-delay: -0.75s;
  animation-delay: -0.75s;
  -webkit-transform: rotate(360deg) translate(0, -150%);
  transform: rotate(360deg) translate(0, -150%);
}
.ispinner.animating .ispinner-blade {
  -webkit-animation-play-state: running;
  animation-play-state: running;
}
.ispinner.white .ispinner-blade {
  background-color: white;
}
.ispinner.gray .ispinner-blade {
  background-color: #8c8c8c;
}
.ispinner.large {
  width: 35px;
  height: 35px;
}
.ispinner.large .ispinner-blade {
  width: 8.57143%;
  height: 25.71429%;
  border-radius: 50%/16.67%;
}

@-webkit-keyframes ispinner-fade {
  0% {
    opacity: 0.85;
  }
  50% {
    opacity: 0.25;
  }
  100% {
    opacity: 0.25;
  }
}
@keyframes ispinner-fade {
  0% {
    opacity: 0.85;
  }
  50% {
    opacity: 0.25;
  }
  100% {
    opacity: 0.25;
  }
}
</style>
